import React, { useState, useEffect } from "react";
import { ModalX } from "../../components";
import { Alert, Button, Checkbox, IconButton, Input, Option, Select, Spinner, Tab, TabPanel, Tabs, TabsBody, TabsHeader, Textarea, Chip } from "@material-tailwind/react";
import { CalendarIcon, Edit2Icon, EyeIcon, ListIcon, PlusIcon, UserIcon, TrashIcon } from "lucide-react";
import { AddNewMeetingRoom, AddNewMeetingRoomBooking, CancelMeetingRoomBooking, GetFloors, GetMeetingRoomBookings, GetMeetingRooms, UpdateMeetingRoom, UpdateMeetingRoomBooking } from "../../services/backend";
import moment from "moment";
import { getOrdinal, minutesToText, Notify, NotifyError } from "../../utils/functions";
import { Calendar, momentLocalizer } from 'react-big-calendar'
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './style.css'
import { useGetUsers } from "../../custom-hooks/use-users";
import Autocomplete from '../../components/autocomplete';
import { usePermissions } from "../../custom-hooks/use-permissions";
import { Link } from "react-router-dom";
import { STATIC_USER_ROLES, VERIFICATION_TYPES } from "../../utils/enums";
import { useConfirm } from "../../custom-hooks/confirm-dialog";



const localizer = momentLocalizer(moment)


const INITIAL_FORM_DATA = {
    floor: '',
    number: '',
    code: '',
    capacity: '',
    useableArea: '',
    totalArea: '',
    status: '',
    securityType: '',
    securityLevel: '',
    isAccessible: true,
    isBookable: true,
    dedicatedClimateControl: true,
    dedicatedInternet: false
}

const INITIAL_FORM_DATA_FOR_BOOKING = {
    user: null,
    meetingRoom: '',
    title: '',
    description: '',
    startDate: '',
    slot: '',
}

const formatBookingTextWithCustomerName = (customerName, rawText, fallback = '') => {
    const normalizedName = customerName?.trim();
    const normalizedText = rawText?.trim() || fallback;

    if (!normalizedText) return normalizedText;
    if (!normalizedName) return normalizedText;

    const expectedPrefix = `${normalizedName} - `;
    if (normalizedText.startsWith(expectedPrefix)) {
        return normalizedText;
    }

    return `${normalizedName} - ${normalizedText}`;
};

const toRoleLabel = (role) => {
    if (!role) return 'Unknown';
    return role.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
};

const getCreatorTagText = (booking) => {
    const creatorRole = booking?.createdBy?.role;
    const creatorName = booking?.createdBy?.name;
    const isCreatedByCustomer = booking?.createdBy?._id && booking?.user?._id && String(booking.createdBy._id) === String(booking.user._id);

    if (!creatorName) return null;

    const isCustomerCreated = creatorRole
        ? (creatorRole === STATIC_USER_ROLES.USER || creatorRole === STATIC_USER_ROLES.TEAM_MEMBER)
        : isCreatedByCustomer;
    if (isCustomerCreated) return null;

    if (!creatorRole) {
        return `Created by - ${creatorName}`;
    }

    if (creatorRole === STATIC_USER_ROLES.ADMIN) {
        return `Admin created - ${creatorName}`;
    }

    return `${toRoleLabel(creatorRole)} created - ${creatorName}`;
};

const MeetingRoomPage = () => {
    const { can } = usePermissions();

    // State variables
    // const [branches, setBranches] = useState([]);
    // const [buildings, setBuildings] = useState([]);
    const [floors, setFloors] = useState([]);
    const [meetingRooms, setMeetingRooms] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [formData, setFormData] = useState(INITIAL_FORM_DATA);
    const [isUpdating, setIsUpdating] = useState(false);
    const [selectedMeetingRoomId, setSelectedMeetingRoomId] = useState(null);
    // const [googleMapsLinkStatus, setGoogleMapsLinkStatus] = useState('');
    // const [isLoadingCoordinates, setIsLoadingCoordinates] = useState(false);
    const [refreshRequired, setRefreshRequired] = useState(0);
    const [loading, setLoading] = useState(true);

    const [bookingFormData, setBookingFormData] = useState(INITIAL_FORM_DATA_FOR_BOOKING);
    const [modalOpenForBooking, setModalOpenForBooking] = useState(false);
    // const [users, setUsers] = useState([]);
    const [meetingRoomBookings, setMeetingRoomBookings] = useState([]);
    const [events, setEvents] = useState([]);
    const [view, setView] = useState('month');
    const [loadingMutation, setLoadingMutation] = useState(false);
    const [isUpdatingBooking, setIsUpdatingBooking] = useState(false);
    const [isViewingPastBooking, setIsViewingPastBooking] = useState(false);
    const [selectedBookingId, setSelectedBookingId] = useState(null);
    const [originalBookingData, setOriginalBookingData] = useState(null);
    const { confirm, ConfirmationDialog } = useConfirm();

    // Daily usage isn't tracked as a running counter server-side (only monthly is) — derive
    // it from today's bookings for the selected room, across the whole org (the customer plus
    // any of their team members), same shared pool the monthly total draws from. Memoized:
    // this iterates every fetched booking, and the booking modal re-renders on every keystroke
    // (title, description, etc.) — without memoizing, that recomputation ran on every keystroke
    // and was the actual cause of typing lag, not the size of the fetched booking list itself.
    const roomDailyUsedMinutes = React.useMemo(() => {
        const selectedUser = bookingFormData.user?.userObj;
        if (!bookingFormData.meetingRoom || !selectedUser) return 0;
        const isTeamMember = selectedUser.role === STATIC_USER_ROLES.TEAM_MEMBER;
        const poolOwnerId = isTeamMember ? (selectedUser.teamId?._id || selectedUser.teamId) : selectedUser._id;
        const targetDate = bookingFormData.startDate ? moment(bookingFormData.startDate) : moment();
        return meetingRoomBookings
            .filter(b => b.status !== VERIFICATION_TYPES.CANCELLED)
            .filter(b => {
                const bUserId = b.user?._id || b.user;
                const bUserObj = typeof b.user === 'object' ? b.user : null;
                const bTeamId = bUserObj?.teamId?._id || bUserObj?.teamId;
                const isSameOrg = String(bUserId) === String(poolOwnerId) || String(bTeamId) === String(poolOwnerId);
                const isSameRoom = String(b.meetingRoom?._id || b.meetingRoom) === String(bookingFormData.meetingRoom);
                const isSameDay = moment(b.startDate).isSame(targetDate, 'day');
                const isCurrentBooking = isUpdatingBooking && String(b._id) === String(selectedBookingId);
                return isSameOrg && isSameRoom && isSameDay && !isCurrentBooking;
            })
            .reduce((sum, b) => sum + (b.slot || 0), 0);
    }, [meetingRoomBookings, bookingFormData.meetingRoom, bookingFormData.user, bookingFormData.startDate, isUpdatingBooking, selectedBookingId]);

    const { data: users, isLoading: isLoadingUsers } = useGetUsers({
        _limit: 1000,
        // role: [STATIC_USER_ROLES.USER]
    })

    useEffect(() => {
        const fetchMeetingRooms = async () => {
            setLoading(true);
            const response = await GetMeetingRooms();
            setMeetingRooms(response.data.meetingRooms);
            setLoading(false);
        }
        const fetchFloors = async () => {
            const response = await GetFloors();
            setFloors(response.data.floors);
        }
        const fetchMeetingRoomBookings = async () => {
            const response = await GetMeetingRoomBookings();
            setMeetingRoomBookings(response.data.meetingRoomBookings);
            const events = response.data.meetingRoomBookings.filter((booking) => booking.status !== VERIFICATION_TYPES.CANCELLED).map((booking) => ({
                id: booking._id,
                title: booking.title,
                start: new Date(booking.startDate),
                end: new Date(booking.endDate),
                resourceId: booking.meetingRoom?._id,
                color: booking.meetingRoom?.idColor,
                description: booking.description,
                slot: booking.slot,
                userObj: booking.user,
            }));
            setEvents(events);
        }
        fetchMeetingRoomBookings();
        // fetchUsers();
        fetchMeetingRooms();
        fetchFloors();
    }, [refreshRequired]);

    // Handle form input change
    const handleInputChange = async (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSelectChange = (name, value) => {
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleCheckBoxChange = async (e) => {
        const { name, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: checked,
        }));
    };

    // Handle form submission
    const handleFormSubmit = async (e) => {
        e.preventDefault();
        try {

            if (isUpdating) {
                // Update building logic
                const updatedMeetingRoom = {
                    floor: formData.floor,
                    number: formData.number,
                    code: formData.code,
                    idColor: formData.idColor,
                    capacity: formData.capacity,
                    useableArea: formData.useableArea,
                    totalArea: formData.totalArea,
                    status: formData.status,
                    securityType: formData.securityType,
                    securityLevel: formData.securityLevel,
                    isAccessible: formData.isAccessible,
                    isBookable: formData.isBookable,
                    dedicatedClimateControl: formData.dedicatedClimateControl,
                    dedicatedInternet: formData.dedicatedInternet,
                };
                await UpdateMeetingRoom(selectedMeetingRoomId, updatedMeetingRoom);
                // setFloors((prev) => prev.map((floor) => (floor._id === selectedFloorId ? updatedFloor : floor)));
                setRefreshRequired(refreshRequired + 1);
                Notify('Meeting Room Updated Successfully', 'success');
            } else {
                // Add new building logic
                const newMeetingRoom = {
                    floor: formData.floor,
                    number: formData.number,
                    code: formData.code,
                    capacity: formData.capacity,
                    useableArea: formData.useableArea,
                    totalArea: formData.totalArea,
                    status: formData.status,
                    securityType: formData.securityType,
                    securityLevel: formData.securityLevel,
                    isAccessible: formData.isAccessible,
                    isBookable: formData.isBookable,
                    dedicatedClimateControl: formData.dedicatedClimateControl,
                    dedicatedInternet: formData.dedicatedInternet,
                };
                await AddNewMeetingRoom(formData.floor, newMeetingRoom)
                // setFloors((prev) => [...prev, newFloor]);
                setRefreshRequired(refreshRequired + 1);
                Notify('Meeting Room Added Successfully', 'success');
            }
        } catch (error) {
            console.error(error);
            NotifyError(error.response.data.message, error);
        } finally {
            setModalOpen(false);
            setFormData(INITIAL_FORM_DATA);
            setIsUpdating(false);
            setSelectedMeetingRoomId(null);
        }
    };

    // Open modal for adding a new building
    const openAddMeetingRoomModal = () => {
        setModalOpen(true);
        const defaultValueForInitialFormData = { ...INITIAL_FORM_DATA };
        defaultValueForInitialFormData.status = 'operational';
        defaultValueForInitialFormData.securityType = 'shared-fingerprint';
        defaultValueForInitialFormData.securityLevel = 'medium';

        setFormData(defaultValueForInitialFormData);
        setIsUpdating(false);
    };

    // Open modal for updating a building
    const openUpdateMeetingRoomModal = (meetingRoom) => {
        setModalOpen(true);
        setFormData({ ...meetingRoom, floor: meetingRoom.floor._id });
        setIsUpdating(true);
        setSelectedMeetingRoomId(meetingRoom._id);
    };

    const handleOnTimeSlotSelect = (slotInfo) => {
        if (view === 'day' || view === 'week') {
            setIsUpdatingBooking(false);
            setIsViewingPastBooking(false);
            setBookingFormData({
                ...INITIAL_FORM_DATA_FOR_BOOKING,
                startDate: slotInfo.start,
                meetingRoom: slotInfo.resourceId,
            });
            setModalOpenForBooking(true);
        }
        else if (view === 'month') {
            setView('day');
        }
    };

    const handleOnEventSelect = (event) => {
        const formData = {
            user: event.userObj ? {
                value: event.userObj._id,
                label: <div className='flex items-center justify-between gap-2'>
                    <div className='flex items-center gap-2'>
                        <div className='w-4 h-4 rounded-full'>
                            <UserIcon size={12} />
                        </div>
                        {event.userObj.name}
                    </div>
                    <div className='text-xs text-gray-500'>
                        {event.userObj.email}
                    </div>
                </div>,
                searchLabel: event.userObj.name + ' (' + event.userObj.email + ')',
                userName: event.userObj.name
            } : null,
            meetingRoom: event.resourceId,
            title: event.title,
            description: event.description,
            startDate: event.start,
            slot: String(event.slot),
        };
        const isPastOrStarted = !moment(event.start).isAfter(moment());
        setBookingFormData(formData);
        setOriginalBookingData(formData);
        setIsUpdatingBooking(true);
        setIsViewingPastBooking(isPastOrStarted);
        setSelectedBookingId(event.id);
        setModalOpenForBooking(true);
    };

    const handleBookingFormSubmit = async (e) => {
        e.preventDefault();
        try {
            if (!bookingFormData.user) return NotifyError('Please select a customer', 'Validation Error');
            if (!bookingFormData.meetingRoom) return NotifyError('Please select a meeting room', 'Validation Error');
            if (!bookingFormData.title) return NotifyError('Please enter a title', 'Validation Error');
            if (!bookingFormData.startDate) return NotifyError('Please select a start time', 'Validation Error');
            if (!bookingFormData.slot) return NotifyError('Please select a duration (slot)', 'Validation Error');

            if (!isUpdatingBooking && moment(bookingFormData.startDate).isBefore(moment())) {
                const confirmation = await confirm({
                    title: 'Create booking in the past?',
                    message: 'Are you sure you want to create booking in the past? It will not be editable.',
                    confirmText: 'Yes, Create',
                    cancelText: 'No, Cancel',
                    confirmBtnColor: 'amber',
                });

                if (!confirmation) {
                    return;
                }
            }

            setLoadingMutation(true);
            const customerName = bookingFormData.user?.userName || '';
            const bookingTitle = formatBookingTextWithCustomerName(customerName, bookingFormData.title);
            const bookingDescription = formatBookingTextWithCustomerName(customerName, bookingFormData.description, 'No Description');
            if (isUpdatingBooking) {
                const updatedMeetingRoomBooking = {
                    user: bookingFormData.user?.value || null,
                    meetingRoom: bookingFormData.meetingRoom,
                    title: bookingTitle,
                    description: bookingDescription,
                    startDate: bookingFormData.startDate,
                    slot: bookingFormData.slot,
                };
                await UpdateMeetingRoomBooking(selectedBookingId, updatedMeetingRoomBooking);
                setRefreshRequired(refreshRequired + 1);
                Notify('Meeting Room Booking Updated Successfully', 'success');
            } else {
                const newMeetingRoomBooking = {
                    user: bookingFormData.user?.value || null,
                    meetingRoom: bookingFormData.meetingRoom,
                    title: bookingTitle,
                    description: bookingDescription,
                    startDate: bookingFormData.startDate,
                    slot: bookingFormData.slot,
                };
                await AddNewMeetingRoomBooking(newMeetingRoomBooking);
                setRefreshRequired(refreshRequired + 1);
                Notify('Meeting Room Booked Successfully', 'success');
            }
            setModalOpenForBooking(false);
            setBookingFormData(INITIAL_FORM_DATA_FOR_BOOKING);
            setIsUpdatingBooking(false);
            setIsViewingPastBooking(false);
            setSelectedBookingId(null);
            setOriginalBookingData(null);
        }
        catch (error) {
            console.error(error);
            NotifyError(error.response.data.message, error);
        } finally {
            setLoadingMutation(false);
        }
    }

    const handleCancelBooking = async (bookingId) => {
        const confirmation = await confirm({
            title: 'Are you sure you want to cancel this booking?',
            message: 'This action cannot be undone.',
            confirmText: 'Yes, Cancel',
            cancelText: 'No, Keep it',
            confirmBtnColor: 'red',
        });

        if (confirmation) {
            try {
                await CancelMeetingRoomBooking(bookingId);
                setRefreshRequired(refreshRequired + 1);
                Notify('Meeting Room Booking Cancelled Successfully', 'success');
            }
            catch (error) {
                console.error(error);
                NotifyError(error.response.data.message, error);
            }
        }
    }

    const normalizeBookingFormDataForCompare = (formData) => ({
        user: formData?.user?.value || formData?.user || null,
        meetingRoom: formData?.meetingRoom || null,
        title: formData?.title || '',
        description: formData?.description || '',
        slot: formData?.slot || '',
        startDate: formData?.startDate ? new Date(formData.startDate).getTime() : null,
    });

    const isFormChanged = isUpdatingBooking ?
        JSON.stringify(normalizeBookingFormDataForCompare(bookingFormData)) !== JSON.stringify(normalizeBookingFormDataForCompare(originalBookingData))
        : true;

    // Memoized: this scans all 48 half-hour slots against every fetched event for the
    // selected room, and was previously a plain function called on every render — including
    // every keystroke while typing elsewhere in the same modal (title, description, etc.),
    // since it wasn't gated behind any memoization. At up to a few hundred fetched bookings
    // that's tens of thousands of moment.js object constructions per keystroke, which is what
    // caused the perceptible typing lag — not the booking list size itself. Also precomputes
    // the relevant events' start/end as plain timestamps once instead of reconstructing moment
    // objects for the same events on every one of the 48 slots.
    const availableTimeOptions = React.useMemo(() => {
        const options = [];
        const relevantEvents = (bookingFormData.meetingRoom && bookingFormData.startDate)
            ? events
                .filter(event => event.resourceId === bookingFormData.meetingRoom)
                .filter(event => !(isUpdatingBooking && event.id === selectedBookingId))
                .map(event => ({ start: new Date(event.start).getTime(), end: new Date(event.end).getTime() }))
            : [];
        const baseDate = bookingFormData.startDate ? moment(bookingFormData.startDate) : null;

        for (let i = 0; i < 48; i++) {
            const hours = Math.floor(i / 2);
            const minutes = (i % 2) * 30;
            const timeValue = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;

            let isAvailable = true;
            if (baseDate) {
                const optionTime = baseDate.clone().set({ hour: hours, minute: minutes, second: 0, millisecond: 0 }).valueOf();
                isAvailable = !relevantEvents.some(ev => optionTime >= ev.start && optionTime < ev.end);
            }
            if (isAvailable) {
                options.push(
                    <Option key={timeValue} value={timeValue}>
                        {moment().hour(hours).minute(minutes).format('hh:mm A')}
                    </Option>
                );
            }
        }
        return options;
    }, [bookingFormData.meetingRoom, bookingFormData.startDate, events, isUpdatingBooking, selectedBookingId]);

    // react-big-calendar re-lays-out every event on every render of its parent, regardless
    // of whether its own props changed — the booking modal lives in this same component and
    // re-renders it on every keystroke, so without this the calendar was redoing that layout
    // work (over ~200 events) on every character typed. Memoizing the element itself makes
    // React skip re-rendering this subtree unless one of these actual dependencies changes.
    const canBook = can('FX8', 'booking');
    const calendarElement = React.useMemo(() => (
        <Calendar
            localizer={localizer}
            events={events}
            eventPropGetter={(event) => {
                return {
                    className: 'text-xs font-bold cursor-pointer',
                    style: {
                        backgroundColor: event.color || 'rgb(228, 148, 0)',
                        borderRadius: '0',
                        border: '0px solid rgb(228, 148, 0)',
                    }
                }
            }}
            selectable={canBook && (view === 'day' || view === 'week' || view === 'month')}
            onSelectSlot={handleOnTimeSlotSelect}
            onSelectEvent={handleOnEventSelect}
            longPressThreshold={10}
            onView={setView}
            view={view}
            startAccessor="start"
            endAccessor="end"
            style={{ height: 700 }}
            resources={meetingRooms.map((meetingRoom) => ({
                id: meetingRoom?._id,
                title: meetingRoom?.code
            }))}
        />
    ), [events, view, meetingRooms, canBook]);

    return (
        <div className={`p-4`}>
            <div className="flex justify-between items-center mb-4">
                <h1 className="md:text-2xl font-thin">Meeting Room Calendar</h1>

            </div>
            <div className="p-4 border rounded-3xl">
                <div>
                    <Tabs value="calendar">
                        <TabsHeader className='bg-gray-200 border border-gray-200 shadow-none rounded-2xl'>
                            <Tab value="calendar">
                                <div className='flex items-center gap-2 font-thin'>
                                    <CalendarIcon className='w-4 h-4' /> Calendar
                                </div>
                            </Tab>
                            <Tab value="list">
                                <div className='flex items-center gap-2 font-thin'>
                                    <ListIcon className='w-4 h-4' /> List
                                </div>
                            </Tab>
                        </TabsHeader>
                        <TabsBody>
                            <TabPanel value="calendar">
                                {calendarElement}
                            </TabPanel>
                            <TabPanel value="list">
                                {
                                    loading ?
                                        <div className="flex justify-center">
                                            <Spinner />
                                        </div> : (meetingRoomBookings.length === 0 ?
                                            <div className="flex justify-center mt-4">
                                                <Alert color="brown" variant="outlined">
                                                    Click on the Date to book a Meeting Room.
                                                </Alert>
                                            </div> :
                                            <div className="overflow-x-auto max-h-[700px] overflow-y-auto">
                                                <table className="w-full min-w-max table-auto text-left">
                                                    <thead className="text-sm">
                                                        <tr className="py-3">
                                                            <th colSpan={4} className="py-3 text-xs font-light text-orange-900">List of All Bookings</th>
                                                        </tr>
                                                        <tr className="py-3 uppercase">
                                                            <th className="py-3">Meeting Room</th>
                                                            <th className="py-3">Booked For</th>
                                                            <th className="py-3">Booked By</th>
                                                            <th className="py-3">Booking Time</th>
                                                            <th className="py-3">Booking Duration</th>
                                                            <th className="py-3">Actions</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {meetingRoomBookings.map((meetingRoomBooking, index) => (
                                                            <tr key={index} draggable className="border-t hover:bg-gray-50 button rounded-full">
                                                                <td className="font-thin py-3 text-sm">
                                                                    <div className="text-sm font-bold">
                                                                        <div className="flex items-center gap-1">
                                                                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: meetingRoomBooking.meetingRoom?.idColor || 'gray' }}></div>
                                                                            <div>{meetingRoomBooking.meetingRoom?.code || 'N/A'}</div>
                                                                            <div className="text-xs text-gray-500">
                                                                                {meetingRoomBooking.meetingRoom?.floor?.floorCode || 'N/A'}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <div className="text-xs text-gray-500">
                                                                        {meetingRoomBooking.meetingRoom?.floor?.building?.name} - {meetingRoomBooking.meetingRoom?.floor?.building?.branch?.name}
                                                                        {meetingRoomBooking.meetingRoom?.floor?.building?.name} - {meetingRoomBooking.meetingRoom?.floor?.building?.branch?.name}
                                                                    </div>
                                                                </td>
                                                                <td className="py-3 text-sm">
                                                                    <Link to={`/customer/${meetingRoomBooking.user?._id}`}
                                                                        className='text-sm font-thin text-blue-600 hover:text-orange-600 duration-300 underline flex items-center gap-1'>
                                                                        <UserIcon size={12} /> {meetingRoomBooking.user?.name || "Unknown User"}
                                                                    </Link>
                                                                </td>
                                                                <td className="font-thin py-3 text-sm">
                                                                    {getCreatorTagText(meetingRoomBooking) ? (
                                                                        <Chip color='blue-gray' value={getCreatorTagText(meetingRoomBooking)} className='w-fit normal-case' />
                                                                    ) : (
                                                                        <div className='text-xs font-thin text-gray-500'>
                                                                            -
                                                                        </div>
                                                                    )}
                                                                </td>
                                                                <td className="font-thin py-3 text-sm">
                                                                    <div className='text-sm font-bold'>
                                                                        {moment(meetingRoomBooking.startDate).format('LT')} - {moment(meetingRoomBooking.endDate).format('LT')}
                                                                    </div>
                                                                    <div className='text-xs text-gray-500'>
                                                                        {moment(meetingRoomBooking.createdDate).format('lll')}
                                                                    </div>
                                                                </td>
                                                                <td className="font-thin py-3 text-sm">
                                                                    {minutesToText(meetingRoomBooking.slot)}
                                                                </td>
                                                                <td className="font-thin py-3 text-sm">
                                                                    {
                                                                        meetingRoomBooking.status !== VERIFICATION_TYPES.CANCELLED ?
                                                                            moment(meetingRoomBooking.startDate).isAfter(moment()) ?
                                                                                <div className='flex gap-1 items-center'>
                                                                                    {can('FX8', 'booking') && (
                                                                                        <IconButton size='sm' color='blue' className='rounded-full' onClick={() => {
                                                                                            const userObj = meetingRoomBooking.user;
                                                                                            const formData = {
                                                                                                user: userObj ? {
                                                                                                    value: userObj._id,
                                                                                                    label: <div className='flex items-center justify-between gap-2'>
                                                                                                        <div className='flex items-center gap-2'>
                                                                                                            <div className='w-4 h-4 rounded-full'>
                                                                                                                <UserIcon size={12} />
                                                                                                            </div>
                                                                                                            {userObj.name}
                                                                                                        </div>
                                                                                                        <div className='text-xs text-gray-500'>
                                                                                                            {userObj.email}
                                                                                                        </div>
                                                                                                    </div>,
                                                                                                    searchLabel: (userObj.name || '') + ' (' + (userObj.email || '') + ')',
                                                                                                    userName: userObj.name || '',
                                                                                                    userObj: userObj
                                                                                                } : null,
                                                                                                meetingRoom: meetingRoomBooking.meetingRoom?._id,
                                                                                                title: meetingRoomBooking.title,
                                                                                                description: meetingRoomBooking.description,
                                                                                                startDate: new Date(meetingRoomBooking.startDate),
                                                                                                slot: String(meetingRoomBooking.slot),
                                                                                            };
                                                                                            setBookingFormData(formData);
                                                                                            setOriginalBookingData(formData);
                                                                                            setIsUpdatingBooking(true);
                                                                                            setIsViewingPastBooking(false);
                                                                                            setSelectedBookingId(meetingRoomBooking._id);
                                                                                            setModalOpenForBooking(true);
                                                                                        }}>
                                                                                            <Edit2Icon className='w-4 h-4' />
                                                                                        </IconButton>
                                                                                    )}
                                                                                    {can('FX8', 'booking') && (
                                                                                        <IconButton size='sm' color='red' className='rounded-full' onClick={() => handleCancelBooking(meetingRoomBooking._id)}>
                                                                                            <TrashIcon className='w-4 h-4' />
                                                                                        </IconButton>
                                                                                    )}
                                                                                </div> :
                                                                                <IconButton size='sm' color='blue-gray' className='rounded-full' onClick={() => {
                                                                                    const userObj = meetingRoomBooking.user;
                                                                                    const formData = {
                                                                                        user: userObj ? {
                                                                                            value: userObj._id,
                                                                                            label: <div className='flex items-center justify-between gap-2'>
                                                                                                <div className='flex items-center gap-2'>
                                                                                                    <div className='w-4 h-4 rounded-full'>
                                                                                                        <UserIcon size={12} />
                                                                                                    </div>
                                                                                                    {userObj.name}
                                                                                                </div>
                                                                                                <div className='text-xs text-gray-500'>
                                                                                                    {userObj.email}
                                                                                                </div>
                                                                                            </div>,
                                                                                            searchLabel: (userObj.name || '') + ' (' + (userObj.email || '') + ')',
                                                                                            userName: userObj.name || '',
                                                                                            userObj: userObj
                                                                                        } : null,
                                                                                        meetingRoom: meetingRoomBooking.meetingRoom?._id,
                                                                                        title: meetingRoomBooking.title,
                                                                                        description: meetingRoomBooking.description,
                                                                                        startDate: new Date(meetingRoomBooking.startDate),
                                                                                        slot: String(meetingRoomBooking.slot),
                                                                                    };
                                                                                    setBookingFormData(formData);
                                                                                    setOriginalBookingData(formData);
                                                                                    setIsUpdatingBooking(true);
                                                                                    setIsViewingPastBooking(true);
                                                                                    setSelectedBookingId(meetingRoomBooking._id);
                                                                                    setModalOpenForBooking(true);
                                                                                }}>
                                                                                    <EyeIcon className='w-4 h-4' />
                                                                                </IconButton>
                                                                            :
                                                                            <div className='flex items-center gap-2'>
                                                                                <Chip color='yellow' value='Cancelled' />
                                                                            </div>
                                                                    }
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>

                                                </table>
                                            </div>)
                                }
                            </TabPanel>
                        </TabsBody>
                    </Tabs>
                </div>


                <ModalX
                    isOpen={modalOpenForBooking}
                    onClose={() => setModalOpenForBooking(false)}
                    title={
                        <div className='flex items-center justify-between gap-2 w-full'>
                            <div className='text-lg font-thin'>{isViewingPastBooking ? "Viewing a past booking" : isUpdatingBooking ? "Update Meeting Room Booking" : "Book Meeting Room"}</div>
                            <div className='text-md font-thin'>
                                {moment(bookingFormData.startDate).format('h:mm A')} - {
                                    bookingFormData.slot ?
                                        moment(bookingFormData.startDate).add(bookingFormData.slot, 'minutes').format('h:mm A') :
                                        <span>Slot {!isViewingPastBooking && <span className="text-red-500">*</span>}</span>

                                }
                                <div className='text-xs text-orange-900'>
                                    {moment(bookingFormData.startDate).format('LL')}
                                </div>
                            </div>
                        </div>
                    }
                    maxWidth="xl">
                    <form onSubmit={handleBookingFormSubmit}>
                        <div className="space-y-2">
                            <div>
                                <Autocomplete
                                    placeholder={<>Select Customer {!isViewingPastBooking && <span className="text-red-500 ml-1">*</span>}</>}
                                    isLoading={isLoadingUsers}
                                    isDisabled={isViewingPastBooking}
                                    options={
                                        users?.users?.map((user) => ({
                                            value: user._id,
                                            userName: user.name,
                                            label: <div className='flex items-center justify-between gap-2'>
                                                <div className='flex items-center gap-2'>
                                                    <div className='w-4 h-4 rounded-full'>
                                                        <UserIcon size={12} />
                                                    </div>
                                                    {user.name}
                                                </div>
                                                <div className='text-xs text-gray-500'>
                                                    {user.email}
                                                </div>
                                            </div>,
                                            searchLabel: user.name + ' (' + user.email + ')',
                                            userObj: user
                                        })) || []}
                                    isClearable={true}
                                    required={!isViewingPastBooking}
                                    getOptionLabel={(option) => option.label}
                                    getOptionValue={(option) => option.searchLabel}
                                    onChange={(newValue) => {
                                        if (isViewingPastBooking) return;
                                        setBookingFormData({ ...bookingFormData, user: newValue });
                                    }}
                                    value={bookingFormData.user}
                                />
                            </div>
                            <div>
                                <Select
                                    label={<>Select Meeting Room {!isViewingPastBooking && <span className="text-red-500 ml-1">*</span>}</>}
                                    value={bookingFormData.meetingRoom}
                                    disabled={isViewingPastBooking}
                                    onChange={newValue => setBookingFormData({ ...bookingFormData, meetingRoom: newValue })}
                                >
                                    {
                                        meetingRooms.map((meetingRoom, index) => (
                                            <Option key={index} value={meetingRoom._id}>
                                                {meetingRoom.number} ({meetingRoom.code}) in {meetingRoom.floor.building.name}
                                            </Option>
                                        ))
                                    }
                                </Select>
                                {/* Per-room quota info for selected user */}
                                {bookingFormData.meetingRoom && bookingFormData.user?.userObj && [STATIC_USER_ROLES.USER, STATIC_USER_ROLES.TEAM_MEMBER].includes(bookingFormData.user.userObj.role) && (
                                    (() => {
                                        const selectedUser = bookingFormData.user.userObj;
                                        const selectedRoomQuota = selectedUser.roomQuotas?.find(rq => rq.room?.toString() === bookingFormData.meetingRoom?.toString());
                                        const isTeamMember = selectedUser.role === STATIC_USER_ROLES.TEAM_MEMBER;
                                        const roomDailyLeft = selectedRoomQuota && selectedRoomQuota.dailyMinutes > 0
                                            ? Math.max(0, selectedRoomQuota.dailyMinutes - roomDailyUsedMinutes)
                                            : null;

                                        return (
                                            <div className='mt-3 mb-2 rounded-xl border border-blue-100 bg-gradient-to-r from-blue-50 to-blue-50/50 p-3 shadow-sm'>
                                                <div className="flex flex-col md:flex-row gap-3">
                                                    {(() => {
                                                        const pct = selectedRoomQuota && selectedRoomQuota.monthlyMinutes > 0 ? Math.round((selectedRoomQuota.usedMinutes / selectedRoomQuota.monthlyMinutes) * 100) : 0;
                                                        const colorClass = pct >= 100 ? 'text-red-700' : pct >= 75 ? 'text-yellow-700' : 'text-blue-700';
                                                        return (
                                                            <>
                                                                {selectedRoomQuota && selectedRoomQuota.monthlyMinutes > 0 && (
                                                                    <div className="flex-1 flex justify-between items-center text-sm p-2.5 bg-white rounded-lg border border-blue-50 shadow-sm">
                                                                        <span className="font-medium text-blue-900">{isTeamMember ? "Organization's limit for this room left:" : "Customer's monthly limit for this room left:"}</span>
                                                                        <span className={`font-bold ${colorClass}`}>
                                                                            {((selectedRoomQuota.monthlyMinutes - selectedRoomQuota.usedMinutes) / 60).toFixed(1)}hr
                                                                        </span>
                                                                    </div>
                                                                )}
                                                                {selectedRoomQuota && selectedRoomQuota.dailyMinutes > 0 && (
                                                                    <div className="flex-1 flex justify-between items-center text-sm p-2.5 bg-white rounded-lg border border-blue-50 shadow-sm">
                                                                        <span className="font-medium text-blue-900">{isTeamMember ? "Organization's daily limit left for this room:" : "Customer's daily limit left for this room:"}</span>
                                                                        <span className={`font-bold ${roomDailyLeft === 0 ? 'text-red-700' : 'text-blue-700'}`}>
                                                                            {(roomDailyLeft / 60).toFixed(1)}hr
                                                                        </span>
                                                                    </div>
                                                                )}
                                                            </>
                                                        )
                                                    })()}
                                                    {(() => {
                                                        const totalMonthlyHours = selectedUser.meetingRoomQuota?.monthlyHours || 0;
                                                        const totalUsedHours = selectedUser.meetingRoomQuota?.usedHours || 0;
                                                        const totalPct = totalMonthlyHours > 0 ? Math.round((totalUsedHours / totalMonthlyHours) * 100) : 0;
                                                        const totalColorClass = totalPct >= 100 ? 'text-red-700' : totalPct >= 75 ? 'text-yellow-700' : 'text-blue-700';
                                                        return (
                                                            <div className="flex-1 flex justify-between items-center text-sm p-2.5 bg-white rounded-lg border border-blue-50 shadow-sm">
                                                                <span className="font-medium text-blue-900">{isTeamMember ? "Team member's monthly limit left for all meeting rooms:" : "Customer's monthly limit left for all meeting rooms:"}</span>
                                                                <span className={`font-bold ${totalColorClass}`}>
                                                                    {(((totalMonthlyHours) - (totalUsedHours)) / 60).toFixed(1)}hr
                                                                </span>
                                                            </div>
                                                        )
                                                    })()}
                                                </div>
                                            </div>
                                        )
                                    })()
                                )}
                            </div>
                            <div>
                                <Input
                                    label="Title"
                                    name="title"
                                    required={!isViewingPastBooking}
                                    disabled={isViewingPastBooking}
                                    value={bookingFormData.title}
                                    onChange={(e) => setBookingFormData({ ...bookingFormData, title: e.target.value })}
                                />
                            </div>
                            <div>
                                <Textarea
                                    label="Description"
                                    name="description"
                                    disabled={isViewingPastBooking}
                                    value={bookingFormData.description}
                                    onChange={(e) => setBookingFormData({ ...bookingFormData, description: e.target.value })}
                                />
                            </div>
                            <div className="flex gap-2 flex-col md:flex-row">
                                <div className="flex-1">
                                    <Select
                                        label={<>Start Time {!isViewingPastBooking && <span className="text-red-500 ml-1">*</span>}</>}
                                        value={bookingFormData.startDate ? moment(bookingFormData.startDate).format("HH:mm") : ""}
                                        disabled={isViewingPastBooking}
                                        menuProps={{ className: "max-h-60" }}
                                        onChange={(newValue) => {
                                            if (newValue) {
                                                const [hours, minutes] = newValue.split(':');
                                                const newStartDate = moment(bookingFormData.startDate).set({ hour: parseInt(hours), minute: parseInt(minutes), second: 0, millisecond: 0 }).toDate();
                                                setBookingFormData({ ...bookingFormData, startDate: newStartDate });
                                            }
                                        }}
                                    >
                                        {availableTimeOptions}
                                    </Select>
                                </div>
                                <div className="flex-1">
                                    <Select
                                        label={<>Slot {!isViewingPastBooking && <span className="text-red-500 ml-1">*</span>}</>}
                                        value={bookingFormData.slot}
                                        disabled={isViewingPastBooking}
                                        onChange={newValue => setBookingFormData({ ...bookingFormData, slot: newValue })}
                                    >
                                        <Option value={'30'}>30 Minutes</Option>
                                        <Option value={'60'}>1 Hour</Option>
                                        <Option value={'90'}>1 Hour, 30 Minutes</Option>
                                        <Option value={'120'}>2 Hours</Option>
                                    </Select>
                                </div>
                            </div>
                        </div>
                        <div className="mt-4 flex justify-end space-x-2 items-center w-full">
                            {can('FX8', 'booking') && isUpdatingBooking && !isViewingPastBooking && (
                                <div className="flex-1">
                                    <Button
                                        onClick={() => handleCancelBooking(selectedBookingId).then(() => { setModalOpenForBooking(false); setIsUpdatingBooking(false); })}
                                        color="red"
                                        className="rounded-full flex gap-2 items-center"
                                        disabled={loadingMutation}
                                        variant="outlined"
                                        size="sm"
                                    >
                                        <TrashIcon size={14} /> Cancel Booking
                                    </Button>
                                </div>
                            )}
                            <Button
                                onClick={() => {
                                    setModalOpenForBooking(false);
                                    setIsUpdatingBooking(false);
                                    setIsViewingPastBooking(false);
                                    setBookingFormData(INITIAL_FORM_DATA_FOR_BOOKING);
                                    setSelectedBookingId(null);
                                    setOriginalBookingData(null);
                                }}
                                className="rounded-full "
                                disabled={loadingMutation}
                                variant="text"
                            >
                                Close
                            </Button>
                            {!isViewingPastBooking && can('FX8', 'booking') && (
                                <Button type="submit" className="rounded-full" disabled={loadingMutation || !isFormChanged || !bookingFormData.slot || bookingFormData.slot === 'no_limit' || !bookingFormData.title?.trim() || !bookingFormData.meetingRoom || !bookingFormData.startDate} loading={loadingMutation}>
                                    {isUpdatingBooking ? "Update Booking" : "Book"}
                                </Button>
                            )}
                        </div>
                    </form>
                </ModalX>

                {/* Modal for Add/Update MeetingRoom */}
                <ModalX isOpen={modalOpen}
                    onClose={() => setModalOpen(false)}
                    title={isUpdating ? "Update MeetingRoom" : "Add New MeetingRoom"}
                    maxWidth="xxl">
                    <form onSubmit={handleFormSubmit}>
                        <div className="space-y-2">
                            <div >
                                <Select
                                    label={<>Select Floor <span className="text-red-500 ml-1">*</span></>}
                                    value={formData.floor}
                                    onChange={(newValue) => handleSelectChange("floor", newValue)}
                                >
                                    {
                                        floors.map((floor, index) => (
                                            <Option key={index} value={floor._id}>
                                                <div className="flex justify-between gap-3 w-full">
                                                    <div className="">
                                                        {getOrdinal(floor.number)} Floor - {floor.floorCode} in ({floor.building.name})
                                                    </div>
                                                    <div className="">
                                                        {floor.building.branch.name}
                                                    </div>
                                                </div>
                                            </Option>
                                        ))
                                    }
                                </Select>
                            </div>
                            <div className="flex gap-2 flex-col md:flex-row">
                                <Input
                                    label="MeetingRoom Number"
                                    name="number"
                                    value={formData.number}
                                    onChange={handleInputChange}
                                    required
                                />
                                <Input
                                    label="MeetingRoom Code"
                                    name="code"
                                    value={formData.code}
                                    onChange={handleInputChange}
                                    required
                                />
                                <Input
                                    label="MeetingRoom Color"
                                    name="idColor"
                                    value={formData.idColor}
                                    type="color"
                                    onChange={handleInputChange}
                                // required
                                />

                            </div>

                            <div className="flex gap-2 flex-col md:flex-row">
                                <Input
                                    label="Person Capacity"
                                    name="capacity"
                                    type="number"
                                    value={formData.capacity}
                                    onChange={handleInputChange}
                                />
                                <Input
                                    label="Usable Area (sq/ft)"
                                    name="useableArea"
                                    type="number"
                                    value={formData.useableArea}
                                    onChange={handleInputChange}
                                />
                                <Input
                                    label="Total Area (sq/ft)"
                                    name="totalArea"
                                    type="number"
                                    value={formData.totalArea}
                                    onChange={handleInputChange}
                                />
                            </div>
                            <div className="flex gap-2 flex-col md:flex-row">
                                <Select
                                    label="Status"
                                    value={formData.status}
                                    onChange={newValue => handleSelectChange("status", newValue)}
                                >
                                    <Option value={'operational'}>Operational</Option>
                                    <Option value={'in_maintenance'}>In Maintenance</Option>
                                    <Option value={'under_construction'}>Under Construction</Option>
                                    <Option value={'closed'}>Closed</Option>
                                </Select>
                                <Select
                                    label="Security Level"
                                    value={formData.securityLevel}
                                    onChange={newValue => handleSelectChange("securityLevel", newValue)}
                                >
                                    <Option value={'low'}>Low</Option>
                                    <Option value={'medium'}>Medium</Option>
                                    <Option value={'high'}>High</Option>
                                </Select>
                                <Select
                                    label="Security Type"
                                    value={formData.securityType}
                                    onChange={newValue => handleSelectChange("securityType", newValue)}
                                >
                                    <Option value={'face-detection'}>Face Detection</Option>
                                    <Option value={'fingerprint'}>Fingerprint</Option>
                                    <Option value={'shared-fingerprint'}>Shared Fingerprint</Option>
                                </Select>
                            </div>

                            {/* <div className="">
                <span className="text-md font-thin text-gray-900">
                  Location
                </span>
              </div> */}
                            {/* <div className="flex gap-4">
                <Input
                  label="Google Maps Link"
                  name="googleMapsLink"
                  value={formData.googleMapsLink}
                  onChange={handleInputChange}
                  icon={formData.googleMapsLink !== "" ? (googleMapsLinkStatus ? <Check size={12} /> : <ShieldAlert size={12} />) : <></>}
                />

                <Input
                  label="Longitude"
                  name="lng"
                  value={formData.lng}
                  onChange={handleInputChange}
                  icon={isLoadingCoordinates ? <Spinner /> : <></>}
                />
                <Input
                  label="Latitude"
                  name="lat"
                  value={formData.lat}
                  onChange={handleInputChange}
                  type="number"
                  icon={isLoadingCoordinates ? <Spinner /> : <></>}
                />
              </div> */}

                            <div className="">

                                <Checkbox
                                    name="isBookable"
                                    checked={formData.isBookable}
                                    className='h-4 w-4 p-0' iconProps={{ className: 'p-0' }} labelProps={{ className: 'p-0 text-xs' }}
                                    label="Is Bookable"
                                    onChange={handleCheckBoxChange}
                                />
                                <Checkbox
                                    name="isAccessible"
                                    checked={formData.isAccessible}
                                    className='h-4 w-4 p-0' iconProps={{ className: 'p-0' }} labelProps={{ className: 'p-0 text-xs' }}
                                    label="Is Accessible"
                                    onChange={handleCheckBoxChange}
                                />
                                <Checkbox
                                    name="dedicatedInternet"
                                    checked={formData.dedicatedInternet}
                                    className='h-4 w-4 p-0' iconProps={{ className: 'p-0' }} labelProps={{ className: 'p-0 text-xs' }}
                                    label="Dedicated Internet"
                                    onChange={handleCheckBoxChange}
                                />
                                <Checkbox
                                    name="dedicatedClimateControl"
                                    checked={formData.dedicatedClimateControl}
                                    className='h-4 w-4 p-0' iconProps={{ className: 'p-0' }} labelProps={{ className: 'p-0 text-xs' }}
                                    label="Dedicated Climate Control"
                                    onChange={handleCheckBoxChange}
                                />
                            </div>
                        </div>
                        <div className="mt-4 flex justify-end space-x-2">
                            <Button
                                onClick={() => setModalOpen(false)}
                                className="rounded-full "
                                variant="text"
                            >
                                Cancel
                            </Button>
                            <Button type="submit" className="rounded-full">
                                {isUpdating ? "Update" : "Add"}
                            </Button>
                        </div>
                    </form>
                </ModalX>
            </div>
            <div className={`p-4 border rounded-3xl mt-2`}>
                <div className="flex justify-between items-center mb-4">
                    <h1 className="md:text-2xl font-thin">Manage Meeting Rooms</h1>
                    {can('FX8', 'management') && (
                        <>
                            <IconButton disabled={loading} className="rounded-full md:hidden" size="sm" onClick={openAddMeetingRoomModal}>
                                <PlusIcon size={12} />
                            </IconButton>
                            <Button disabled={loading} className="rounded-full hidden md:block" onClick={openAddMeetingRoomModal}>
                                Add New Meeting Room
                            </Button>
                        </>
                    )}
                </div>
                {
                    loading ?
                        <div className="flex justify-center">
                            <Spinner />
                        </div> : (meetingRooms.length === 0 ?
                            <div className="flex justify-center mt-4">
                                <Alert color="brown" variant="outlined">
                                    Click on the button to add a Meeting Room.
                                </Alert>
                            </div> :
                            <div className="overflow-x-auto">
                                <table className="w-full min-w-max table-auto text-left">
                                    <thead className="text-sm">
                                        <tr className="py-3">
                                            <th colSpan={4} className="py-3 text-xs font-light text-orange-900">List of All Meeting Rooms</th>
                                            {/* <th colSpan={2} className="py-3">
                                    <Input
                                        label="Search"
                                    />
                                </th> */}
                                        </tr>
                                        <tr className="py-3 uppercase">
                                            <th className="py-3">#</th>
                                            <th className="py-3">Location</th>
                                            <th className="py-3">Status</th>
                                            {/* <th className="py-3">Modified</th> */}
                                            <th className="py-3">Created</th>
                                            <th className="py-3">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {meetingRooms.map((meetingRoom, index) => (
                                            <tr key={index} draggable className="border-t hover:bg-gray-50 button rounded-full">
                                                <td className="font-thin py-3 text-sm">
                                                    <div className="flex items-center gap-2">
                                                        <div className="w-4 h-4 rounded-full" style={{ backgroundColor: meetingRoom.idColor }}></div>
                                                        {meetingRoom.number} ({meetingRoom.code})

                                                    </div>
                                                </td>
                                                <td className="font-thin py-3 text-sm">
                                                    <div className="">
                                                        {meetingRoom.floor.number} ({meetingRoom.floor.floorCode}) - {meetingRoom.floor.building.name}
                                                    </div>
                                                    <div className="">
                                                        {meetingRoom.floor.building.branch.name}
                                                    </div>
                                                </td>
                                                <td className="font-thin py-3 text-sm">
                                                    {meetingRoom.status}
                                                </td>
                                                {/* <td className="font-thin py-3 text-sm">
                                        {moment(meetingRoom.lastUpdated).format('lll')}
                                    </td> */}
                                                <td className="font-thin py-3 text-sm">
                                                    {moment(meetingRoom.createdDate).format('lll')}
                                                </td>
                                                <td className="flex gap-1 py-3">
                                                    {/* <IconButton
                                            size="sm"
                                            className="rounded-full"
                                            onClick={() => openUpdateMeetingRoomModal(meetingRoom)}
                                        >
                                            <EyeIcon size={12} />
                                        </IconButton> */}
                                                    {can('FX8', 'management') && (
                                                        <IconButton
                                                            size="sm"
                                                            className="rounded-full"
                                                            onClick={() => openUpdateMeetingRoomModal(meetingRoom)}
                                                        >
                                                            <Edit2Icon size={12} />
                                                        </IconButton>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>

                                </table>
                            </div>)
                }
            </div>
            <ConfirmationDialog />
        </div>
    );
};

export default MeetingRoomPage;
